import { useCallback } from "react"

import {
  CreateAndApplySiteLabelMutation,
  LabelColor,
  useApplyLabelToSitesMutation,
  useCreateAndApplySiteLabelMutation,
  useRemoveLabelFromSitesMutation,
} from "@/v2/graphql/public-generated-rq"
import { useToast } from "@lumen-energy/design-system"

import useOrgLabelsOps from "./useOrgLabelsOps"

interface UseSiteLabelOpsProps {
  // function to refetch site labels for all sites in scope to make sure the label updates and
  // deletions are reflected for the `LabelGroup`'s site.
  refetchSiteLabels?: () => void
  orgSlug?: string
}

/**
 * Hooks to handle operations on Site Labels for a given site.
 */
const useSiteLabelsOps = ({ refetchSiteLabels, orgSlug }: UseSiteLabelOpsProps) => {
  const toast = useToast()

  const { refetchOrgSiteLabels } = useOrgLabelsOps({
    refetchSites: refetchSiteLabels,
    orgSlug,
  })

  const mutateAndRefetch = useCallback(
    async <T,>(mutationPromise: Promise<T>) => {
      await mutationPromise
      await refetchOrgSiteLabels()
      refetchSiteLabels?.()
    },
    [refetchOrgSiteLabels, refetchSiteLabels],
  )

  const createAndApplySiteLabelMutation = useCreateAndApplySiteLabelMutation()
  const handleCreateAndApplySiteLabel = useCallback(
    async ({
      sitePid,
      labelName,
      labelColor,
      callback,
    }: {
      sitePid: string
      labelName: string
      labelColor: LabelColor
      callback?: (result: CreateAndApplySiteLabelMutation) => void
    }) => {
      if (!orgSlug) {
        return
      }
      const promise = createAndApplySiteLabelMutation.mutateAsync({
        input: {
          slug: orgSlug,
          labelColor,
          labelName,
          sitePid,
        },
      })

      toast.promise(promise, {
        loading: `Adding "${labelName}" label...`,
        success: `"${labelName}" label added`,
        error: `Error adding "${labelName}" label`,
      })
      mutateAndRefetch(promise)

      if (callback) {
        await promise.then((result) => callback(result))
      }
    },
    [createAndApplySiteLabelMutation, mutateAndRefetch, toast, orgSlug],
  )

  // Apply an already-existing label to multiple sites
  const applyLabelToSitesMutation = useApplyLabelToSitesMutation()
  const handleApplyLabelToSites = useCallback(
    async ({
      siteLabelPid,
      sitePids,
      labelName,
    }: {
      siteLabelPid: string
      sitePids: Array<string>
      labelName: string
    }) => {
      if (!orgSlug) {
        return
      }
      const promise = applyLabelToSitesMutation.mutateAsync({
        input: { slug: orgSlug, siteLabelPid, sitePids },
      })

      toast.promise(promise, {
        loading: `Adding "${labelName}" label...`,
        success: `"${labelName}" label added`,
        error: `Error adding "${labelName}" label`,
      })
      mutateAndRefetch(promise)
    },
    [applyLabelToSitesMutation, mutateAndRefetch, toast, orgSlug],
  )

  // Apply an already-existing label to a single site
  const handleApplyLabelToSite = useCallback(
    async ({
      siteLabelPid,
      sitePid,
      labelName,
    }: {
      siteLabelPid: string
      sitePid: string
      labelName: string
    }) => {
      if (!orgSlug) {
        return
      }
      const promise = applyLabelToSitesMutation.mutateAsync({
        input: { slug: orgSlug, siteLabelPid, sitePids: [sitePid] },
      })

      toast.promise(promise, {
        loading: `Adding "${labelName}" label...`,
        success: `"${labelName}" label added`,
        error: `Error adding "${labelName}" label`,
      })
      mutateAndRefetch(promise)
    },
    [applyLabelToSitesMutation, mutateAndRefetch, toast, orgSlug],
  )

  const removeLabelFromSitesMutation = useRemoveLabelFromSitesMutation()

  const handleRemoveLabel = useCallback(
    async ({
      siteLabelPid,
      sitePid,
      labelName,
    }: {
      siteLabelPid: string
      sitePid: string
      labelName: string
    }) => {
      if (!orgSlug) {
        return
      }
      const promise = removeLabelFromSitesMutation.mutateAsync({
        input: { slug: orgSlug, sitePids: [sitePid], siteLabelPid },
      })

      toast.promise(promise, {
        loading: `Removing "${labelName}" label...`,
        success: `"${labelName}" label removed`,
        error: `Error removing "${labelName}" label`,
      })
      mutateAndRefetch(promise)
    },
    [mutateAndRefetch, removeLabelFromSitesMutation, toast, orgSlug],
  )

  const handleRemoveLabelFromSites = useCallback(
    async ({
      siteLabelPid,
      sitePids,
      labelName,
    }: {
      siteLabelPid: string
      sitePids: Array<string>
      labelName: string
    }) => {
      if (!orgSlug) {
        return
      }
      const promise = removeLabelFromSitesMutation.mutateAsync({
        input: { slug: orgSlug, sitePids, siteLabelPid },
      })

      toast.promise(promise, {
        loading: `Removing "${labelName}" label...`,
        success: `"${labelName}" label removed`,
        error: `Error removing "${labelName}" label`,
      })
      mutateAndRefetch(promise)
    },
    [mutateAndRefetch, removeLabelFromSitesMutation, toast, orgSlug],
  )
  return {
    handleApplyLabelToSite,
    handleApplyLabelToSites,
    handleCreateAndApplySiteLabel,
    handleRemoveLabel,
    handleRemoveLabelFromSites,
  }
}

export default useSiteLabelsOps
